import { type fabric } from 'fabric';
import type WhiteboardController from '../controllers/WhiteboardController';
import {
  hasRecordReference,
  hasSmartZoneBehaviors,
  isGroupLike,
} from '../objects/guards';
import { type SmartZoneObject } from '../objects/SmartZoneBehaviors';
import { pluralize } from '@aha-app/aha-shared';
import type {
  RecordUpdate,
  RecordUpdateResponse,
  SmartZoneAttributes,
} from '../shared-types';
import { randomId } from '@aha-app/mvc';
import { type RecordReference } from '../types';

/**
 * Finds all smart zones at a specific point on the canvas
 */
export function findSmartZonesAtPoint(
  canvas: fabric.Canvas,
  point: fabric.IPoint
) {
  // Get all objects with smart zone behaviors enabled
  const smartZoneObjects = canvas
    .getObjects()
    .filter(hasSmartZoneBehaviors)
    .filter(obj => obj.smartZoneConfig?.enabled);

  return smartZoneObjects.filter(
    smartZone =>
      // @ts-expect-error TODO correct the type of containsPoint - I don't think it needs a Point object, just {x, y}
      smartZone.everContainsPoint?.(point) ?? smartZone.containsPoint(point)
  );
}

interface AttributesByType {
  [recordType: string]: SmartZoneAttributes;
}

/**
 * Gets the combined attributes from all smart zones at a point for a specific record type
 */
export function getSmartZoneAttributes(
  zones: SmartZoneObject[]
): AttributesByType {
  // Combine attributes from all smart zones, with top-most taking precedence
  // Objects are already sorted by z-index
  const attributes: AttributesByType = {};

  for (const smartZone of zones) {
    const configurations = smartZone.smartZoneConfig?.configurations ?? {};
    for (const [recordType, config] of Object.entries(configurations)) {
      if (config.enabled) {
        attributes[recordType] = {
          ...(attributes[recordType] ?? {}),
          ...config.attributes,
        };
      }
    }
  }

  return attributes;
}

/**
 * Process an object that has been moved to check if it's in any smart zones
 * and should have attributes applied
 */
export async function applySmartZonesAtPoint(
  controller: WhiteboardController,
  obj: fabric.Object,
  mousePoint: fabric.IPoint
) {
  const { updateRecords } = controller.props;

  if (!updateRecords) {
    console.debug('No updateRecords function provided, skipping');
    return;
  }

  const smartZones = findSmartZonesAtPoint(controller.canvas, mousePoint);
  const attributesByType = getSmartZoneAttributes(smartZones);

  const objectsToUpdate: (fabric.Object & RecordReference)[] = [];

  if (isGroupLike(obj)) {
    obj.forEachObjectDeep(child => {
      if (hasRecordReference(child)) {
        objectsToUpdate.push(child);
      }
    });
  } else if (hasRecordReference(obj)) {
    objectsToUpdate.push(obj);
  }

  if (!objectsToUpdate.length) {
    console.debug('No objects with record references found, skipping');
    return;
  }

  const updates = objectsToUpdate
    .filter(child => !!attributesByType[child.recordType])
    .map(child => ({
      recordType: child.recordType,
      recordId: child.recordId,
      attributes: attributesByType[child.recordType],
    }));

  if (!Object.keys(updates).length) {
    console.debug('No attributes to apply', smartZones);
    return;
  }

  const result = await updateRecords(updates);
  const message = getUpdateMessage(result);
  controller.actionShowMessage(message);
}

/**
 * Determine which eligible records are contained within the given smart zone and
 * reapplies all updates to them from the zone's config.
 */
export async function applyUpdatesForSmartZone(
  controller: WhiteboardController,
  smartZone: SmartZoneObject
) {
  const { updateRecords } = controller.props;

  if (!updateRecords) {
    console.debug('No updateRecords function provided, skipping');
    return;
  }

  if (!smartZone.smartZoneConfig?.enabled) {
    console.debug('Smart zone is not enabled, skipping');
    return { success: false };
  }

  const attributesByType = getSmartZoneAttributes([smartZone]);

  if (!Object.keys(attributesByType).length) {
    console.debug('No enabled configurations in smart zone, skipping');
    return { success: false };
  }

  const updates: RecordUpdate[] = [];

  // Add object to updates if it is eligible and there are attributes to apply
  const processObject = (obj: fabric.Object) => {
    if (
      hasRecordReference(obj) &&
      attributesByType[obj.recordType] &&
      smartZone.containsObject(obj)
    ) {
      updates.push({
        recordType: obj.recordType,
        recordId: obj.recordId,
        attributes: attributesByType[obj.recordType],
      });
    }
  };

  for (const obj of controller.canvas.getObjects()) {
    if (isGroupLike(obj)) {
      obj.forEachObjectDeep(processObject);
    } else {
      processObject(obj);
    }
  }

  if (!updates.length) {
    console.debug('No updates to apply', smartZone);
    return { success: false };
  }

  const result = await updateRecords(updates);
  const message = getUpdateMessage(result);
  controller.actionShowMessage(message);
}

const getUpdateMessage = (result: RecordUpdateResponse) => {
  const successCount = result.results.filter(record => record.success).length;
  const failed = result.results.filter(record => !record.success);
  const failCount = failed.length;

  for (const failure of failed) {
    console.warn(
      `Failed to apply smart zone attributes to ${failure.recordType}/${failure.recordId}`,
      failure.errors
    );
  }

  let message: string;
  if (successCount && !failCount) {
    message = `Smart zone attributes applied successfully to ${successCount} ${pluralize(
      successCount,
      'record'
    )}`;
  } else if (failCount && !successCount) {
    message = `Failed to apply updates to ${failCount} ${pluralize(
      failCount,
      'record'
    )}`;
  } else if (successCount && failCount) {
    message = `${successCount} record ${pluralize(
      successCount,
      'update'
    )} succeeded, ${failCount} failed`;
  } else {
    message = 'No updates applied';
  }

  if (result.warnings?.length) {
    message += `. ${result.warnings.join('. ')}`;
  }

  return message;
};

export const exampleSmartZone = (currentSmartZones: SmartZoneObject[]) => {
  const id = randomId();
  const name = `Smart zone ${currentSmartZones.length + 1}`;
  return {
    objects: {
      [id]: {
        type: 'frame',
        id,
        textContent: name,
        left: 0,
        top: 0,
        width: 400,
        height: 500,
        scaleX: 1,
        scaleY: 1,
        fill: '#FFFFFF',
        opacity: 1,
        smartZoneConfig: { enabled: true },
      },
    },
  };
};
